import { writable, get } from 'svelte/store';
import type { Readable } from 'svelte/store';
import type { PagiflowInstance, PagiflowOptions } from './types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PagiflowState {
  instance: PagiflowInstance | null;
  currentIndex: number;
}

export interface PagiflowController extends Readable<PagiflowState> {
  /** Svelte action — attach with `use:slider.action` on the track element */
  action(node: HTMLElement): { destroy(): void };
  /** Go to next slide */
  next(): void;
  /** Go to previous slide */
  prev(): void;
  /** Go to a specific slide */
  goTo(index: number, opts?: { silent?: boolean; instant?: boolean }): void;
  /** Start autoplay */
  play(): void;
  /** Pause autoplay */
  pause(): void;
  /** Toggle play / pause */
  togglePlayPause(): void;
  /** Update options (optionally rebuild) */
  setOptions(opts: Partial<PagiflowOptions>, rebuild?: boolean): void;
  /** Destroy and reinitialise */
  reInit(opts?: Partial<PagiflowOptions>): void;
}

// ─── Loader ───────────────────────────────────────────────────────────────────

function getPagiflow(): ((selector: string | HTMLElement, opts?: PagiflowOptions) => PagiflowInstance) | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('pagiflow');
    return mod.default ?? mod;
  } catch {
    return null;
  }
}

// ─── createPagiflow ───────────────────────────────────────────────────────────

/**
 * Creates a reactive Pagiflow controller.
 *
 * @example
 * ```svelte
 * <script>
 *   import { createPagiflow } from 'pagiflow/svelte';
 *   const slider = createPagiflow({ loop: true, nav: true });
 *   $: currentIndex = $slider.currentIndex;
 * </script>
 *
 * <div use:slider.action>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 * </div>
 *
 * <button on:click={slider.prev}>‹</button>
 * <button on:click={slider.next}>›</button>
 * ```
 */
export function createPagiflow(options: PagiflowOptions = {}): PagiflowController {
  const store = writable<PagiflowState>({
    instance: null,
    currentIndex: options.startIndex ?? 0,
  });

  // ─── Svelte action ─────────────────────────────────────────────────────────
  function action(node: HTMLElement): { destroy(): void } {
    const existing = (node as any).pagiflowInstance as PagiflowInstance | undefined;
    if (existing && !existing.destroyed) {
      return { destroy() {} };
    }

    const Pagiflow = getPagiflow();
    if (!Pagiflow) {
      console.error('[createPagiflow] Pagiflow core not found. Install pagiflow or load it via CDN.');
      return { destroy() {} };
    }

    // Use a unique data attribute so the selector is always scoped
    const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
    const selector = `[data-pagiflow-svelte-id="${attrValue}"]`;
    node.setAttribute('data-pagiflow-svelte-id', attrValue);

    const inst = Pagiflow(selector, options) as PagiflowInstance | null;
    if (!inst) {
      node.removeAttribute('data-pagiflow-svelte-id');
      return { destroy() {} };
    }

    const syncLayout = () => { (inst as any).update?.(); };

    const raf1 = requestAnimationFrame(() => syncLayout());
    const raf2 = requestAnimationFrame(() => syncLayout());
    const timeoutId = setTimeout(() => syncLayout(), 80);
    window.addEventListener('load', syncLayout, { once: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => syncLayout());
      resizeObserver.observe(node);
    }

    inst.onSlideChange((idx) => {
      store.update((s) => ({ ...s, currentIndex: idx }));
    });

    store.update((s) => ({ ...s, instance: inst }));

    return {
      destroy() {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
        clearTimeout(timeoutId);
        window.removeEventListener('load', syncLayout);
        resizeObserver?.disconnect();

        const current = get(store).instance;
        if (current && !current.destroyed) {
          try { current.destroy(); } catch { /* ignore */ }
        }
        delete (node as any).pagiflowInstance;
        store.update((s) => ({ ...s, instance: null }));
      },
    };
  }

  // ─── Stable API ────────────────────────────────────────────────────────────
  function getInstance(): PagiflowInstance | null {
    return get(store).instance;
  }

  return {
    subscribe: store.subscribe,
    action,
    next:           () => getInstance()?.next(),
    prev:           () => getInstance()?.prev(),
    goTo:           (i, o) => getInstance()?.goTo(i, o),
    play:           () => getInstance()?.play(),
    pause:          () => getInstance()?.pause(),
    togglePlayPause:() => getInstance()?.togglePlayPause(),
    setOptions:     (o, r) => getInstance()?.setOptions(o, r),
    reInit:         (o) => getInstance()?.reInit(o),
  };
}
